<template>
  <section class="form-list">
    <el-form
      class="form"
      ref="form"
      :rules="rules"
      :model="student"
      label-position="right"
      label-width="130px"
    >
      <el-form-item label="姓名" prop="student_name">
        <el-input v-model="student.student_name" maxlength="14" placeholder="请输入学员真实姓名" show-word-limit clearable/>
      </el-form-item>

      <v-input-phone v-model="student.phone"/>

      <v-image-upload
        v-model="student.selfie"
        label="真人自拍"
        prefix="student"
        ratio="1:1"
        prop-name="selfie"
      />

      <el-form-item label="性别">
        <el-radio-group v-model="student.gender" size="medium">
          <el-radio :label="0" border>保密</el-radio>
          <el-radio :label="1" border>男生</el-radio>
          <el-radio :label="2" border>女生</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="生日" prop="birthday">
        <el-date-picker
          v-model="student.birthday"
          :picker-options="pickerOptionsBirthday"
          align="right"
          type="date"
          placeholder="请选择生日"
          class="date-picker"
          value-format="timestamp"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="实名证件号" prop="id_number">
        <el-input v-model="student.id_number" maxlength="18" placeholder="请填写身份证、护照号等" show-word-limit clearable/>
      </el-form-item>

      <el-form-item label="购买课卡">
        <el-switch v-model="student.is_buy_card" :active-value="1" :inactive-value="0"/>
      </el-form-item>

      <template v-if="student.is_buy_card">
        <v-input-card-number v-model="student.card_number"/>
        <el-form-item label="适用门店">
          <v-select-store
            class="w100"
            placeholder="全国通用"
            :multiple="true"
            :is-location-limit="true"
            v-model="student.store_ids"
          />
        </el-form-item>
        <el-form-item label="课卡类型" prop="card_charge_type_id">
          <v-cascader-card-type
            v-model="cardTypeInfo"
            :store-ids="student.store_ids"
            @change="handleCardTypeChange"
            :is-new="true"
          />
        </el-form-item>

        <div v-show="cardTypeInfo.length">
          <el-form-item label="禁止预约专栏">
            <span v-if="student.is_disable_column" class="danger-color">是</span>
            <span v-else class="success-color">否</span>
          </el-form-item>

          <el-form-item label="有效次数" prop="expire_number">
            <v-input-money
              v-if="student.is_number_limit"
              class="w100"
              placeholder="每次约课将根据课程设置的扣卡次数，扣除相应次数"
              v-model="student.expire_number"
            />
            <span v-else class="success-color">期限卡，无次数限制</span>
            <el-alert
              v-if="student.expire_number !== student.expire_number_default"
              :title="`当前输入次数与默认有效次数「 ${student.expire_number_default} 次 」不一致`"
              type="warning"
              show-icon
              :closable="false"
            />
          </el-form-item>

          <el-form-item label="有效天数" prop="expire_days">
            <v-input-number
              class="w100"
              placeholder="开卡后系统将根据有效天数自动计算出失效日期"
              v-model="student.expire_days"
              type="unsignedSmallInt"
            />
            <el-alert
              v-if="student.expire_days !== student.expire_days_default"
              :title="`当前输入天数与默认有效天数「 ${student.expire_days_default} 天 」不一致`"
              type="warning"
              show-icon
              :closable="false"
            />
          </el-form-item>

          <el-form-item
            v-show="student.store_ids.length"
            label="适用门店限制天数"
            prop="store_limit_expire_days"
          >
            <v-input-number
              class="w100"
              placeholder="超过设定的天数后，课卡将自动变为全国通用"
              type="unsignedSmallInt"
              v-model="student.store_limit_expire_days"
            />
          </el-form-item>
        </div>

        <el-form-item label="收款方式" prop="card_payment_mode_id">
          <v-select-card-payment-mode class="w100" v-model="student.card_payment_mode_id"/>
        </el-form-item>

        <v-image-upload
          v-model="student.receipt"
          label="收款凭证"
          prefix="card"
          placeholder="请上传转账截图"
          alert="微信、支付宝转账截图必须显示付款时间、金额、付款学员头像及昵称，现金收款需拍照，否则办卡申请将被驳回。"
          propName="receipt"
        />

        <v-input-you-zan-number
          v-model="student.you_zan_number"
          :card-payment-mode-id="student.card_payment_mode_id"
          @actualPaymentChange="actual_payment => student.actual_payment = student.you_zan_payment = actual_payment"
        />

        <el-form-item v-if="student.card_payment_mode_id === cardPaymentModeIds.youZan" label="有赞核销金额">
          <v-input-money
            class="w100"
            v-model="student.you_zan_payment"
            placeholder="请输入购买有赞的核销金额"
            :is-show-suffix="true"
          />
        </el-form-item>

        <el-form-item label="实际付款">
          <v-input-money
            class="w100"
            placeholder="请输入购买课卡的实际付款"
            v-model="student.actual_payment"
          />
          <el-alert
            v-if="isPriceDiff"
            :title="`应收 ${student.price} 元，实收 ${student.actual_payment} 元，两者不一致`"
            type="warning"
            show-icon
            :closable="false"
          />
        </el-form-item>

        <el-form-item label="付款备注" prop="payment_remark">
          <el-input v-model="student.payment_remark" placeholder="请输入课卡付款备注"/>
        </el-form-item>
        <v-multi-file-upload
          v-model="student.remark_files"
          label="备注文件"
          prefix="card"
        />

        <el-form-item label="可用日期">
          <el-checkbox v-for="v in weeks" :key="v.value" v-model="v.checked">{{ v.label }}</el-checkbox>
          <el-alert
            title="勾选后，可预约对应日期的课程，默认全部可约"
            type="info"
            show-icon
            :closable="false"
          />
        </el-form-item>

        <el-form-item label="办卡日期" prop="register_time">
          <el-date-picker
            v-model="student.register_time"
            :picker-options="pickerOptionsBetweenSystemStartAndToday"
            align="right"
            type="date"
            placeholder="请选择办卡日期，默认为当前日期"
            class="date-picker"
            value-format="timestamp"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="自动开卡日期" prop="start_time_auto">
          <el-alert
            title="新生初次办卡默认 15 天后自动开卡"
            type="warning"
            show-icon
            :closable="false"
          />
        </el-form-item>

        <el-form-item
          v-show="cardTypeInfo.length && student.card_type !== cardTypes.personal.value"
          label="设为默认支付课卡"
        >
          <el-switch v-model="student.is_default_used" :active-value="1" :inactive-value="0"/>
        </el-form-item>

        <template v-if="cardTicketType.id">
          <el-alert
            title="购买本课卡附赠一张课次券"
            type="success"
            show-icon
            :closable="false"
          />

          <div class="form-divider-title">赠券信息</div>

          <el-form-item label="课次券名称">
            <span class="success-color">{{ cardTicketType.card_ticket_type_name }}</span>
          </el-form-item>
          <el-form-item label="有效次数">
            <span class="success-color">{{ cardTicketType.expire_number | toYuan }}</span> 次
          </el-form-item>
          <el-form-item label="有效天数">
            <span class="success-color">{{ cardTicketType.expire_days | toYuan }}</span> 天
          </el-form-item>
        </template>

        <el-form-item v-if="isDiff" label="申请原因" prop="apply_remark">
          <el-input
            type="textarea"
            :autosize="{ minRows: 4 }"
            placeholder="请输入 2 个字符以上的申请原因"
            v-model="student.apply_remark"
            maxlength="250"
            show-word-limit
          />
          <el-alert
            :title="`当前输入的${diffTip}不符，需要提交申请，审核通过后即可成功办理`"
            type="warning"
            show-icon
            :closable="false"
          />
        </el-form-item>
      </template>

      <el-form-item>
        <el-button type="primary" @click="handleSubmit" :loading="isSaving">
          {{ isAdministrator ? '立即' : '申请' }}添加
        </el-button>
        <el-button @click="RouteConfig.studentList.back">取消</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { pickerOptionsBetweenSystemStartAndToday, pickerOptionsBirthday, pickerOptionsOfAfterToday } from '@/utils/formats';
import { cardTypes } from '@/enums/cardTypes';
import { store } from '@/api/student';
import handleCardTypeChange from '@/utils/handleCardTypeChange';
import { toSecond, toCent } from "@/utils/number";
import weeks from "@/enums/weeks";
import cardPaymentModeIds from '@/enums/cardPaymentModeIds';
import { getDetailByCardChargeTypeId } from "@/api/card-ticket-type";

const defaultCardTicketType = {
  id: 0,
  update_time: 0,
  card_ticket_type_name: '',
  expire_days: 0,
  expire_number: 0,
};

export default {
  name: RouteConfig.studentAdd.name,
  data: () => ({
    RouteConfig,
    cardTypes,
    cardPaymentModeIds,
    pickerOptionsBetweenSystemStartAndToday,
    pickerOptionsBirthday,
    pickerOptionsOfAfterToday,
    weeks: JSON.parse(JSON.stringify(weeks)),
    isSaving: false,
    isPhoneSearching: false,
    isCardNumberSearching: false,
    student: {
      student_name: '',
      nickname: '',
      phone: '',
      gender: 0,
      birthday: '',
      id_number: '',
      avatar: '',
      selfie: '',
      used_card_id: '',
      is_buy_card: 1,

      store_id: '',
      store_ids: [],
      card_number: '',
      card_type_id: 0,
      card_type_name: '',
      card_name: '',
      card_charge_type_id: '',
      is_number_limit: 0,
      expire_number: '',
      expire_number_default: '',
      expire_days: '',
      expire_days_default: '',
      store_limit_expire_days: '',
      card_payment_mode_id: '',
      you_zan_number: '',
      price: '',
      actual_payment: '',
      you_zan_payment: '',
      payment_remark: '',
      receipt: '',
      remark_files: [],
      is_default_used: 1,
      is_disable_column: 0,
      disabled_weeks: [],
      apply_remark: '',
    },
    cardTicketType: JSON.parse(JSON.stringify(defaultCardTicketType)),
    cardTypeInfo: [],
    phoneError: '',
    rules: {
      student_name: [{ required: true, message: '请输入姓名', trigger: 'change' }],
      phone: [{ required: true, message: '请输入手机号', trigger: 'change' }],
      selfie: [{ required: true, message: '请上传真人自拍', trigger: 'change' }],
      store_id: [{ required: true, message: '请选择适用门店', trigger: 'change' }],
      card_payment_mode_id: [{ required: true, message: '请选择课卡付款方式', trigger: 'change' }],
      card_charge_type_id: [{ required: true, message: '请选择课程类型', trigger: 'change' }],
      card_number: [{ required: true, message: '请输入课卡编号', trigger: 'change' }],
      you_zan_number: [{ required: true, message: '请输入有赞券码', trigger: 'change' }],
      actual_payment: [{ required: true, message: '请输入实付金额', trigger: 'change' }],
      receipt: [{ required: true, message: '请上传付款凭证图片', trigger: 'change' }],
    },
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
      'userStoreId',
      'isGenericStore',
    ]),
    diffTip() {
      let tips = [];
      if (this.isPriceDiff) tips.push('实际付款与课卡原价');
      if (this.isDaysDiff) tips.push('有效天数与课卡原有效天数');
      if (this.isNumberDiff) tips.push('有效次数与课卡原有效次数');
      return tips.join('、');
    },
    isPriceDiff() {
      if (!this.student.price || +this.student.price <= 0) return false;
      return +this.student.price !== +this.student.actual_payment;
    },
    isDaysDiff() {
      return this.student.expire_days !== this.student.expire_days_default;
    },
    isNumberDiff() {
      return this.student.expire_number !== this.student.expire_number_default;
    },
    isDiff() {
      return !this.isAdministrator
        && (this.isPriceDiff || this.isDaysDiff || this.isNumberDiff)
        && this.student.card_payment_mode_id !== cardPaymentModeIds.youZan;
    },
  },
  mounted() {
    this.student.store_ids = this.isGenericStore ? [] : [this.userStoreId];// 设置一个值 自动获取课卡类型
  },
  methods: {
    handleStoreChange() {
      this.cardTypeInfo = [];
    },
    handleCardTypeChange(cardTypeInfo) {
      handleCardTypeChange(cardTypeInfo, this.student);
      this.getDetailsByCardChargeTypeId();
    },
    async getDetailsByCardChargeTypeId() {
      if (!this.student.card_charge_type_id) return;

      const { isError, data } = await getDetailByCardChargeTypeId(this.student.card_charge_type_id);
      if (isError || !data || !data.id) return this.cardTicketType = JSON.parse(JSON.stringify(defaultCardTicketType));

      const { cardTicketType } = this;
      const updateData = {};
      Object.keys(data).forEach(key => {
        if (key in cardTicketType) updateData[key] = data[key];
      });
      Object.assign(cardTicketType, updateData);
    },
    handleSubmit() {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        this.isSaving = true;
        const student = { ...this.student };
        const { weeks } = this;
        student.register_time = toSecond(student.register_time);
        student.start_time_auto = toSecond(student.start_time_auto);
        student.birthday = toSecond(student.birthday);
        student.expire_number = toCent(student.expire_number);
        student.expire_number_default = toCent(student.expire_number_default);
        student.actual_payment = toCent(student.actual_payment);
        student.you_zan_payment = toCent(student.you_zan_payment);
        student.price = toCent(student.price);
        student.disabled_weeks = [];
        student.is_diff = this.isDiff ? 1 : 0;
        student.card_ticket_type_update_time = this.cardTicketType.update_time;

        for (let i = 0; i < weeks.length; i++) {
          if (!weeks[i].checked) student.disabled_weeks.push(weeks[i].value);
        }

        const { isError } = await store(student);
        this.isSaving = false;
        if (isError) return;
        if (!student.is_buy_card) return RouteConfig.latentStudentList.push();

        return this.isAdministrator ? RouteConfig.studentList.push() : RouteConfig.cardAddApplyList.push();
      });
    },
  },
};
</script>
